import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import React from 'react';
import PropTypes from 'prop-types';
import styles from './legacy-autocomplete.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import ReactUtils from '../../packages/react-utils/react-utils.js';
import '@flixbus/honeycomb-icons-react';
import '../../packages/props-utils/prop-types.js';
import useComponentId from '../../hooks/use-component-id/use-component-id.js';
import { legacyAutocompleteChildrenValidator } from './legacy-autocomplete-children-validator.js';
import { LegacyAutocompleteOptionPropTypes } from './quarks/legacy-autocomplete-option.js';
import { LegacyAutocompleteGroupPropTypes } from './quarks/legacy-autocomplete-option-group.js';
import { LegacyAutocompleteContextProvider } from './legacy-autocomplete.context.js';
import { LegacyAutocompleteInputContextProvider } from './input/legacy-autocomplete-input.context.js';
import { LegacyAutocompleteOptionsContextProvider } from './options/legacy-autocomplete-options.context.js';
import { LegacyAutocompleteSelectedOptionsContextProvider } from './selected-options/legacy-autocomplete-selected-options.context.js';
import { Injecss } from '../../packages/injecss/injecss.js';

// init css class
const bem = new HoneyBem('legacy-autocomplete', styles);
/**
 * @deprecated This component is deprecated since v12.0.0. It is being kept to ease the transition
 * to the new [Autocomplete](/#/Controllers/Autocomplete). If you are using this component, please
 * consider migrating to the new one, as it is easier to use and has more features.
 */
function LegacyAutocomplete(_a) {
  var {
      children,
      options,
      debounce = 300,
      extraClasses = '',
      onChange = () => null,
      onDebounce = () => null,
      onDelete = () => null,
      onReset = () => null,
      onSelect = () => null,
      onKeyDown = () => null,
      onBlur = () => null,
      onFocus = () => null,
      onOptionFocus = () => null,
      optionsSelected = [],
      optionsListVisible: optionsListVisibleProp,
      value = '',
      id
    } = _a,
    rest = __rest(_a, ["children", "options", "debounce", "extraClasses", "onChange", "onDebounce", "onDelete", "onReset", "onSelect", "onKeyDown", "onBlur", "onFocus", "onOptionFocus", "optionsSelected", "optionsListVisible", "value", "id"]);
  const hasOptions = options && options.length >= 1;
  // state setters
  // store timeout for debounce
  const [timer, setTimer] = React.useState(null);
  // input value proxy (to allow overwriting)
  const [inputValue, setInputValue] = React.useState(value);
  // options listbox visibility based on options length, optionsVisible prop make it controllable
  const [optionsListVisible, setOptionsListVisible] = React.useState(hasOptions);
  const isOptionsListVisible = optionsListVisibleProp === undefined ? optionsListVisible : optionsListVisibleProp;
  // holds visible option items
  const [focusableOptions, setFocusableOptions] = React.useState(options && options.map(option => 'options' in option ? option.options.map(entry => entry) : option).flat());
  // stores focused option index allowing for focus control with arrow keys
  const [focusedOption, setFocusedOption] = React.useState(-1);
  const containerRef = React.useRef(null);
  const listBoxRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const containerId = useComponentId('hcr-autocomplete', id);
  const isMultiselect = ReactUtils.getChildrenByName(children, 'LegacyAutocompleteSelectedOptions')[0] !== undefined;
  /**
   * Proxies onchange on input (for debounce)
   * fires `onChange` programmatically
   * @param event
   */
  const handleInputChange = event => {
    event.persist();
    onChange(event);
    setInputValue(event.target.value);
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(window.setTimeout(() => onDebounce(event), debounce));
  };
  /**
   * Runs on option selection
   * Proxies selected option to onSelect callback,
   * Updates focused option index
   * @param selectedOption
   * @param index
   */
  const handleOptionSelect = (selectedOption, index) => {
    onSelect(selectedOption);
    // if the list stays visible after the option selection we want to keep track of focused item
    setFocusedOption(index);
    // clearing the input after selection for multiselect mode
    if (isMultiselect) {
      setInputValue('');
    }
  };
  const handleOptionFocus = (option, optionIndex, event) => {
    setFocusedOption(optionIndex);
    onOptionFocus(option, event);
  };
  // we don't want to hide the list if there is still an element inside of Autocomplete focused
  const handleComponentBlur = event => {
    onBlur(event);
    const origin = event.relatedTarget;
    if (origin && containerRef.current && containerRef.current.contains(origin)) {
      return;
    }
    setOptionsListVisible(false);
  };
  // shows the list again if there are options
  const handleComponentFocus = event => {
    onFocus(event);
    if (event.target === inputRef.current && hasOptions) {
      setOptionsListVisible(true);
    }
  };
  /**
   * Key events handling,
   * mostly used for arrow based focus tracking and "Esc" handling
   * @param event
   */
  const handleKeyDown = event => {
    var _a;
    onKeyDown(event);
    // force closing options list
    if (event.key === 'Escape') {
      setOptionsListVisible(false);
    }
    // perform selection
    if (event.key === 'Enter' && options && focusedOption >= 0) {
      handleOptionSelect(focusableOptions[focusedOption], focusedOption);
    }
    // arrow key handlers only apply if target is the input or in the options list
    if (event.target !== inputRef.current && !((_a = listBoxRef === null || listBoxRef === void 0 ? void 0 : listBoxRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
      return;
    }
    // return early if it is not arrow keys
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    // disable page scroll when using arrow keys to navigate the options list
    event.preventDefault();
    setOptionsListVisible(hasOptions);
    // changing visual focus state of options depending on the arrow key press
    if (isOptionsListVisible && focusableOptions) {
      if (event.key === 'ArrowUp') {
        const nextInteractiveOptionIndex = focusedOption <= 0 ? focusableOptions.length - 1 : focusedOption - 1;
        handleOptionFocus(focusableOptions[nextInteractiveOptionIndex], nextInteractiveOptionIndex, event);
      } else if (event.key === 'ArrowDown') {
        const nextInteractiveOptionIndex = focusedOption < focusableOptions.length - 1 ? focusedOption + 1 : 0;
        // jumping to the start if end is reached
        handleOptionFocus(focusableOptions[nextInteractiveOptionIndex], nextInteractiveOptionIndex, event);
      }
    }
  };
  // overwrite controlled value if prop value changes
  // (allows setting value arbitrarily)
  React.useEffect(() => {
    setInputValue(value);
    setFocusedOption(-1);
  }, [value]);
  React.useEffect(() => {
    // force setting list to visible if any options has been supplied
    setOptionsListVisible(hasOptions);
    // update list of focusable options
    setFocusableOptions(options && options.map(option => 'options' in option ? option.options.map(entry => entry) : option).flat());
  }, [options]);
  // reset focus tracking on option list re-render
  React.useEffect(() => {
    setFocusedOption(-1);
  }, [isOptionsListVisible]);
  return /*#__PURE__*/React.createElement(LegacyAutocompleteContextProvider, {
    containerId: containerId,
    focusableOptions: focusableOptions,
    selectedOptions: optionsSelected,
    focusedOption: focusedOption,
    isMultiselect: isMultiselect
  }, /*#__PURE__*/React.createElement("div", Object.assign({
    id: id,
    className: bem.b(false, extraClasses),
    ref: containerRef,
    onBlur: handleComponentBlur,
    onFocus: handleComponentFocus,
    role: "presentation",
    onKeyDown: handleKeyDown
  }, rest), /*#__PURE__*/React.createElement(LegacyAutocompleteInputContextProvider, {
    optionsListVisible: isOptionsListVisible,
    onChange: handleInputChange,
    value: inputValue,
    inputRef: inputRef
  }, ReactUtils.getChildrenByName(children, 'LegacyAutocompleteInput')[0]), isOptionsListVisible && hasOptions && (/*#__PURE__*/React.createElement(LegacyAutocompleteOptionsContextProvider, {
    options: options,
    listBoxRef: listBoxRef,
    onOptionSelect: handleOptionSelect,
    onOptionFocus: handleOptionFocus
  }, ReactUtils.getChildrenByName(children, 'LegacyAutocompleteOptions')[0])), optionsSelected && optionsSelected.length >= 1 && (/*#__PURE__*/React.createElement(LegacyAutocompleteSelectedOptionsContextProvider, {
    onDelete: onDelete,
    onReset: onReset
  }, ReactUtils.getChildrenByName(children, 'LegacyAutocompleteSelectedOptions')[0]))));
}
LegacyAutocomplete.displayName = 'LegacyAutocomplete';
LegacyAutocomplete.propTypes = {
  id: PropTypes.string,
  children: legacyAutocompleteChildrenValidator(),
  debounce: PropTypes.number,
  extraClasses: PropTypes.string,
  onChange: PropTypes.func,
  onDebounce: PropTypes.func,
  onDelete: PropTypes.func,
  onReset: PropTypes.func,
  onSelect: PropTypes.func,
  onKeyDown: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.arrayOf(LegacyAutocompleteOptionPropTypes), PropTypes.arrayOf(LegacyAutocompleteGroupPropTypes)]).isRequired,
  optionsSelected: PropTypes.arrayOf(LegacyAutocompleteOptionPropTypes),
  optionsListVisible: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};
var legacyAutocomplete = Injecss(LegacyAutocomplete, styles);

export { LegacyAutocomplete, legacyAutocomplete as default };
